<template>
  <div
    v-if="placeTypes.length"
    class="d-flex flex-column ga-3"
    data-qa="lpu_doctors_list_price"
  >
    <div
      v-if="placeTypes.length > 1"
      class="d-flex flex-wrap ga-2"
    >
      <v-chip
        v-for="placeType in placeTypes"
        :key="placeType"
        :value="placeType"
        class="text-none branding-btn"
        :class="placeType === value
          ? 'branding-btn--primary'
          : 'branding-btn--background'"
        @click="emit('input', placeType)"
      >
        {{ tStatic(PLACE_TYPE_LABEL[placeType]) }}
      </v-chip>
    </div>

    <div v-if="isFreeWithTreatment">
      <div class="d-flex align-center ga-3 mb-1">
        <div class="text-h6 text-success--text d-flex align-center ga-1">
          {{ tStatic('Бесплатно') }}
          <v-img
            class="flex-shrink-0"
            width="20"
            height="20"
            contain
            src="/static/_v1/pd/icons/ui-kit/promotion/tag-discount.svg"
            :alt="tStatic('Иконка Акции')"
          />
        </div>
        <div
          v-if="clinicPrice"
          class="text-body-2 text-secondary--text text-decoration-line-through"
        >
          {{ formatClinicPrice }}
        </div>
      </div>
      <div class="text-caption text--text">
        {{ tStatic('Консультация бесплатна при условии дальнейшего лечения. Подробности уточняйте в клинике.') }}
      </div>
    </div>
    <div
      v-else-if="isClubPrice"
      class="d-flex ga-6"
    >
      <div class="d-flex flex-column">
        <div class="d-flex align-center ga-1 text-subtitle-1 font-weight-medium error--text">
          {{ formatClubPrice }}
          <v-img
            class="flex-shrink-0"
            width="24"
            height="24"
            contain
            src="/static/_v1/pd/icons/ui-kit/club/club-percent.svg"
            :alt="tStatic('Иконка Процент Клуба')"
          />
        </div>
        <div class="text-caption error--text">
          {{ tStatic('Клубная цена') }}
        </div>
      </div>
      <div class="d-flex flex-column">
        <div class="text-subtitle-1 text-secondary--text text-decoration-line-through">
          {{ formatClinicPrice }}
        </div>
        <button
          type="button"
          class="text-caption text-secondary--text d-flex align-center pa-0"
          data-qa="lpu_doctors_list_club_info"
          @click="isClubInfoOpen = true"
        >
          {{ tStatic('Подробнее о цене') }}
          <span class="ui-icon-arrow-right ui-icon-fz-smaller" />
        </button>
      </div>
    </div>
    <div v-else-if="activePrice != null">
      <div
        class="text--text"
        :class="value === 'clinic' && activePrice === 0
          ? 'text-h6'
          : 'text-subtitle-1 font-weight-medium mb-1'"
      >
        {{ activePriceLabel }}
      </div>
      <div
        v-if="value === 'clinic' && activePrice"
        class="text-caption text-secondary--text"
      >
        {{ tStatic('Оплата в клинике') }}
      </div>
      <div
        v-else-if="value === 'telemed'"
        class="text-caption text-secondary--text"
      >
        {{ tStatic('Оплата при записи') }}
      </div>
    </div>
    <ClubPriceInfo
      v-if="isClubPrice && clubOfferData"
      v-model="isClubInfoOpen"
      :full-price="clinicPrice || 0"
      :club-offer-data="clubOfferData"
      :skip-window-open="true"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import formatPrice from 'src/functions/formatPrice'
import useContextStore from 'src/store/useContextStore'
import type { CommonContextStore } from 'src/types/context'
import ClubPriceInfo from 'src/components/common/ClubPriceInfo.vue/ClubPriceInfo.vue'
import tStatic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tStatic'
import { PLACE_TYPE_LABEL } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsList/constants'
import getClubOfferData from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsList/functions/getClubOfferData'
import getDoctorPlaceTypes from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsList/functions/getDoctorPlaceTypes'
import type {
  AppointmentPlaceType,
  LpuDoctorMinPrices,
} from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsList/types'

type Emits = {
  (_event: 'input', _value: AppointmentPlaceType): void
}

const props = defineProps<{
  value: AppointmentPlaceType
  minPrices?: LpuDoctorMinPrices | null
}>()

const emit = defineEmits<Emits>()
const { context }: CommonContextStore = useContextStore()
const isClubInfoOpen = ref(false)
const currency = computed(() => context.currency.symbol)
const placeTypes = computed(() => getDoctorPlaceTypes(props.minPrices))
const clinicPrice = computed(() => props.minPrices?.tab_min_price)
const clubPrice = computed(() => props.minPrices?.tab_min_club_price)
const isFreeWithTreatment = computed(() => (
  props.value === 'clinic' && Boolean(props.minPrices?.tab_is_free_with_treatment)
))
const isClubPrice = computed(() => (
  props.value === 'clinic' && typeof clubPrice.value === 'number'
))
const clubOfferData = computed(() => {
  if (typeof clubPrice.value !== 'number') {
    return undefined
  }

  return getClubOfferData(clubPrice.value)
})
const activePrice = computed(() => {
  if (props.value === 'home') {
    return props.minPrices?.tab_min_price_go
  }

  if (props.value === 'telemed') {
    return typeof props.minPrices?.tab_min_price_telemed === 'number'
      ? props.minPrices.tab_min_price_telemed
      : props.minPrices?.tab_min_price_telemed_medtochka
  }

  return clinicPrice.value
})
const formatClinicPrice = computed(() => formatPrice({
  price: clinicPrice.value,
  currencySymbol: currency.value,
}))
const formatClubPrice = computed(() => formatPrice({
  price: clubPrice.value,
  currencySymbol: currency.value,
}))
const activePriceLabel = computed(() => formatPrice({
  price: activePrice.value,
  currencySymbol: currency.value,
  prefix: props.value === 'home' || props.value === 'telemed' ? tStatic('от') : undefined,
  useFreeTextForZero: props.value === 'clinic',
}))
</script>
